import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, Warning, ChatCircle, ShieldWarning, X, Pause, Play, Stop } from "@phosphor-icons/react";

/**
 * ProctorChatPanel
 * Used by the proctor to chat with a student during an active attempt.
 * - Send normal messages
 * - Send formal warnings (counts toward 3-strike auto-submit)
 * - Pause / Resume / End the attempt
 */
export default function ProctorChatPanel({ attemptId, studentName, examTitle, onAutoSubmitted, onEnded, onClose }) {
  const [state, setState] = useState({ messages: [], warning_count: 0, warning_limit: 3, status: "in_progress" });
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [asWarning, setAsWarning] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/proctor/attempts/${attemptId}/messages`);
      setState(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 3000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attemptId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [state.messages.length]);

  const send = async () => {
    const text = input.trim();
    if (!text) return;
    if (asWarning) {
      const remaining = state.warning_limit - state.warning_count;
      if (remaining <= 0) { toast.error("Warning limit already reached"); return; }
      const confirmMsg = remaining === 1
        ? "This will be the FINAL warning — attempt will auto-submit. Continue?"
        : `Send warning ${state.warning_count + 1} of ${state.warning_limit}?`;
      if (!window.confirm(confirmMsg)) return;
    }
    setSending(true);
    try {
      const r = await api.post(`/proctor/attempts/${attemptId}/message`, {
        message: text,
        is_warning: asWarning,
      });
      setInput("");
      setAsWarning(false);
      if (r.data.auto_submitted) {
        toast.error(`${studentName}'s attempt was auto-submitted (3 warnings reached).`);
        onAutoSubmitted?.();
      } else if (asWarning) {
        toast.warning(`Warning ${r.data.warning_count} of ${r.data.warning_limit} sent`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const warningsLeft = Math.max(0, state.warning_limit - state.warning_count);
  const isEnded = state.status === "submitted" || state.status === "expired";
  const isPaused = state.status === "paused";

  const pause = async () => {
    const reason = window.prompt("Reason for pausing (visible to student):", "");
    if (reason === null) return; // cancelled
    try {
      await api.post(`/proctor/attempts/${attemptId}/pause`, { reason });
      toast.success("Exam paused");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to pause"); }
  };
  const resume = async () => {
    try {
      const r = await api.post(`/proctor/attempts/${attemptId}/resume`);
      toast.success(`Exam resumed (+${r.data.extended_seconds || 0}s)`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to resume"); }
  };
  const endAttempt = async () => {
    const reason = window.prompt(
      "End this exam now? The attempt will be scored with current answers and marked as ended by proctor.\n\nEnter a reason (shown to the student):",
      "Ended by proctor due to policy violation",
    );
    if (reason === null) return; // cancelled
    const finalReason = reason.trim() || "Ended by proctor due to policy violation";
    try {
      const r = await api.post(`/proctor/attempts/${attemptId}/end`, { reason: finalReason });
      const pct = r.data?.percentage;
      toast.success(pct != null ? `Exam ended and graded (${pct}%)` : "Exam ended");
      onEnded?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to end"); }
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="proctor-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200 bg-zinc-50">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold flex items-center gap-1.5">
            <ChatCircle size={11} weight="fill" /> Live chat
          </div>
          <div className="font-display text-sm font-bold truncate">{studentName}</div>
          <div className="text-[10px] text-zinc-500 truncate">{examTitle}</div>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 p-1" data-testid="proctor-chat-close">
            <X size={16} />
          </button>
        )}
      </div>

      {/* Warning tally */}
      <div className={`px-4 py-2 border-b border-zinc-200 flex items-center gap-2 ${state.warning_count >= 2 ? "bg-red-50" : state.warning_count >= 1 ? "bg-amber-50" : "bg-white"}`}
        data-testid="proctor-chat-warning-counter">
        <ShieldWarning size={14} className={state.warning_count >= 2 ? "text-red-600" : state.warning_count >= 1 ? "text-amber-600" : "text-zinc-400"} />
        <div className="flex-1 text-[11px] uppercase tracking-wider font-semibold">
          Warnings: <span className="font-mono">{state.warning_count} / {state.warning_limit}</span>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: state.warning_limit }).map((_, i) => (
            <div
              key={i}
              className={`w-4 h-1.5 ${i < state.warning_count ? "bg-red-500" : "bg-zinc-200"}`}
            />
          ))}
        </div>
      </div>

      {/* Actions bar: pause / resume / end */}
      {!isEnded && (
        <div className={`px-3 py-2 border-b border-zinc-200 flex items-center gap-2 ${isPaused ? "bg-amber-50" : "bg-zinc-50"}`} data-testid="proctor-chat-actions">
          {isPaused ? (
            <>
              <div className="flex-1 text-[11px] uppercase tracking-wider font-semibold text-amber-700 inline-flex items-center gap-1">
                <Pause size={11} weight="fill" /> Paused
              </div>
              <button
                onClick={resume}
                className="inline-flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-[11px] px-3 py-1.5 font-semibold"
                data-testid="proctor-resume-btn"
              >
                <Play size={11} weight="fill" /> Resume
              </button>
            </>
          ) : (
            <button
              onClick={pause}
              className="flex-1 inline-flex items-center justify-center gap-1 border border-amber-500 text-amber-700 hover:bg-amber-50 text-[11px] px-3 py-1.5 font-semibold"
              data-testid="proctor-pause-btn"
            >
              <Pause size={11} weight="fill" /> Pause exam
            </button>
          )}
          <button
            onClick={endAttempt}
            className="inline-flex items-center gap-1 border border-red-500 text-red-700 hover:bg-red-50 text-[11px] px-3 py-1.5 font-semibold"
            data-testid="proctor-end-btn-inline"
          >
            <Stop size={11} weight="fill" /> End
          </button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-zinc-50" data-testid="proctor-chat-messages">
        {state.messages.length === 0 && (
          <div className="text-center text-xs text-zinc-500 py-8">
            No messages yet. Start the conversation or issue a warning if you spot a violation.
          </div>
        )}
        {state.messages.map((m) => {
          const isProctor = m.from_role === "proctor" || m.from_role === "institution_admin";
          const isWarn = m.kind === "warning";
          const isSystem = m.kind === "system";
          return (
            <div
              key={m.message_id}
              className={`flex ${isProctor || isSystem ? "justify-end" : "justify-start"}`}
              data-testid={`msg-${m.message_id}`}
            >
              <div
                className={`max-w-[80%] px-3 py-2 text-xs border ${
                  isWarn
                    ? "bg-red-50 border-red-300 text-red-900"
                    : isSystem
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : isProctor
                        ? "bg-zinc-900 text-white border-zinc-900"
                        : "bg-white border-zinc-200 text-zinc-800"
                }`}
              >
                <div className="text-[9px] uppercase tracking-wider opacity-70 mb-1">
                  {m.from_name} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="whitespace-pre-wrap">{m.message}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Composer */}
      {isEnded ? (
        <div className="px-4 py-3 border-t border-zinc-200 text-xs text-zinc-500 text-center bg-zinc-100">
          Attempt {state.status} — chat closed.
        </div>
      ) : (
        <div className="border-t border-zinc-200 bg-white">
          <div className="p-2">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              placeholder={asWarning ? "Describe the violation (student will be warned)…" : "Type a message…"}
              className={`w-full px-2 py-1.5 text-xs focus:outline-none border ${asWarning ? "border-red-400 bg-red-50" : "border-zinc-300 focus:border-zinc-900"}`}
              data-testid="proctor-chat-input"
            />
          </div>
          <div className="flex items-center justify-between px-2 pb-2 gap-2">
            <label className="inline-flex items-center gap-1.5 cursor-pointer" data-testid="proctor-warning-toggle-label">
              <input
                type="checkbox"
                checked={asWarning}
                onChange={(e) => setAsWarning(e.target.checked)}
                disabled={warningsLeft === 0}
                data-testid="proctor-warning-toggle"
              />
              <Warning size={12} className={asWarning ? "text-red-600" : "text-zinc-400"} />
              <span className={`text-[10px] uppercase tracking-wider font-semibold ${asWarning ? "text-red-700" : "text-zinc-600"}`}>
                Send as warning {warningsLeft > 0 ? `(${warningsLeft} left)` : "(limit reached)"}
              </span>
            </label>
            <button
              onClick={send}
              disabled={sending || !input.trim()}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-medium disabled:opacity-50 ${
                asWarning
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-zinc-900 text-white hover:bg-zinc-800"
              }`}
              data-testid="proctor-chat-send"
            >
              <PaperPlaneRight size={11} weight="fill" /> {asWarning ? "Send warning" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
